// Stable auth error codes (sent by server_elakay/elakay_helpers/authErrors.js,
// or detected here for network/timeout failures) -> text in the active Elakay
// language. Pages keep the CODE in their state and call authErrorMessage() at
// render time, so a language switch re-translates an error already on screen
// without repeating the request.
//
// Texts live in the existing catalogue (menuElakay_<lang>.json >
// frontElakayAutoText, keys h_auth_err_*), read through elakayAutoText like
// every other page; the English string below is only the last-resort fallback.
import { elakayAutoText, getElakayLanguageCode } from "../pageInside/dataElakay";

const AUTH_ERROR_TEXT = {
  EMAIL_REQUIRED: ["h_auth_err_email_required", "Please enter your email address."],
  INVALID_EMAIL: ["h_auth_err_invalid_email", "This email address is invalid."],
  EMAIL_LENGTH_INVALID: ["h_auth_err_email_length_invalid", "The email address must contain between 10 and 70 characters."],
  PASSWORD_REQUIRED: ["h_auth_err_password_required", "Please enter your password."],
  PASSWORD_TOO_SHORT: ["h_auth_err_password_too_short", "Your password must contain at least 7 characters."],
  PASSWORD_TOO_LONG: ["h_auth_err_password_too_long", "Your password must contain at most 16 characters."],
  PASSWORD_TOO_WEAK: ["h_auth_err_password_too_weak", "Your password must contain at least one uppercase letter, one lowercase letter and one digit."],
  PASSWORD_CONTAINS_PASSWORD: ["h_auth_err_password_contains_password", "Your password cannot contain the word \"password\"."],
  PASSWORD_CONFIRMATION_MISMATCH: ["h_auth_err_password_confirmation_mismatch", "Passwords do not match."],
  FIRSTNAME_REQUIRED: ["h_auth_err_firstname_required", "Please enter your first name."],
  FIRSTNAME_INVALID: ["h_auth_err_firstname_invalid", "The first name can only contain letters, spaces, hyphens and underscores."],
  FIRSTNAME_TOO_LONG: ["h_auth_err_firstname_too_long", "The first name must contain fewer than 30 characters."],
  LASTNAME_REQUIRED: ["h_auth_err_lastname_required", "Please enter your last name."],
  LASTNAME_INVALID: ["h_auth_err_lastname_invalid", "The last name can only contain letters, spaces, hyphens and underscores."],
  LASTNAME_TOO_LONG: ["h_auth_err_lastname_too_long", "The last name must contain fewer than 30 characters."],
  TERMS_REQUIRED: ["h_auth_err_terms_required", "You must accept the Terms of Service."],
  // Event World account form (pages/roleTokens/eventAccountValidation.js)
  // and the matching server profile checks.
  PASSWORD_CONFIRMATION_REQUIRED: ["h_auth_err_password_confirmation_required", "Please confirm your password."],
  PHONE_REQUIRED: ["h_auth_err_phone_required", "Please enter your phone number."],
  INVALID_PHONE: ["h_auth_err_invalid_phone", "Please enter a valid phone number."],
  ADDRESS_REQUIRED: ["h_auth_err_address_required", "Please enter your address."],
  ADDRESS_INVALID: ["h_auth_err_address_invalid", "The address can only contain letters, numbers, spaces and . , ' # / - (200 characters maximum)."],
  COUNTRY_REQUIRED: ["h_auth_err_country_required", "Please select a country."],
  STATE_REQUIRED: ["h_auth_err_state_required", "Please select or enter your state / province / region."],
  STATE_INVALID: ["h_auth_err_state_invalid", "The state / province / region can only contain letters, numbers, spaces and . , ' - (120 characters maximum)."],
  CITY_REQUIRED: ["h_auth_err_city_required", "Please enter your city."],
  CITY_INVALID: ["h_auth_err_city_invalid", "The city can only contain letters, numbers, spaces and . , ' - (120 characters maximum)."],
  POSTAL_CODE_REQUIRED: ["h_auth_err_postal_code_required", "Please enter your ZIP / postal code."],
  INVALID_POSTAL_CODE: ["h_auth_err_invalid_postal_code", "Please enter a valid ZIP / postal code for this country."],
  INVALID_DATA: ["h_auth_err_invalid_data", "Some of the information entered is invalid."],

  USER_NOT_FOUND: ["h_auth_err_user_not_found", "No account was found with this email address."],
  INVALID_PASSWORD: ["h_auth_err_invalid_password", "Incorrect password."],
  PASSWORD_RESET_REQUIRED: ["h_auth_err_password_reset_required", "This account's password must be reset before you can sign in. Use \"Forgot password\"."],
  ACCOUNT_NOT_ACTIVATED: ["h_auth_err_account_not_activated", "Your account has not been activated yet. Please check your email."],
  ACCOUNT_SUSPENDED: ["h_auth_err_account_suspended", "Your account is suspended."],
  EMAIL_ALREADY_EXISTS: ["h_auth_err_email_already_exists", "An account already exists with this email address."],
  DUPLICATE_VALUE: ["h_auth_err_duplicate_value", "This value is already in use."],

  UNAUTHORIZED: ["h_auth_err_unauthorized", "Your session is invalid or has expired. Please sign in again."],
  INVALID_TOKEN: ["h_auth_err_invalid_token", "This link or session is invalid."],
  TOKEN_EXPIRED: ["h_auth_err_token_expired", "This link or session has expired."],
  RESET_TOKEN_REQUIRED: ["h_auth_err_reset_token_required", "This password reset link is incomplete."],
  INVALID_RESET_TOKEN: ["h_auth_err_invalid_reset_token", "This password reset link is invalid or has already been used."],
  RESET_TOKEN_EXPIRED: ["h_auth_err_reset_token_expired", "This password reset link has expired."],
  ACTIVATION_TOKEN_REQUIRED: ["h_auth_err_activation_token_required", "This activation link is incomplete."],
  INVALID_ACTIVATION_TOKEN: ["h_auth_err_invalid_activation_token", "This activation link is invalid."],
  ACTIVATION_TOKEN_EXPIRED: ["h_auth_err_activation_token_expired", "This activation link has expired."],
  ACCOUNT_ALREADY_ACTIVATED: ["h_auth_err_account_already_activated", "This account is already activated. You can sign in."],
  INVALID_VERIFICATION_CODE_FORMAT: ["h_auth_err_invalid_verification_code_format", "Please enter the 6-digit code."],
  INVALID_VERIFICATION_CODE: ["h_auth_err_invalid_verification_code", "The verification code is incorrect."],
  VERIFICATION_CODE_EXPIRED: ["h_auth_err_verification_code_expired", "Your verification code has expired. Please request a new one."],
  TOO_MANY_VERIFICATION_ATTEMPTS: ["h_auth_err_too_many_verification_attempts", "Too many unsuccessful attempts. Please request a new code."],
  INVALID_DEVICE_ID: ["h_auth_err_invalid_device_id", "This device could not be identified. Please reload the page and try again."],

  ACTIVATION_EMAIL_FAILED: ["h_auth_err_activation_email_failed", "We could not send the activation email. Please try again."],
  VERIFICATION_EMAIL_FAILED: ["h_auth_err_verification_email_failed", "We could not send the verification email. Please try again."],
  PASSWORD_RESET_EMAIL_SENT: ["h_auth_msg_password_reset_email_sent", "We have sent you an email. Open the link it contains to change your password."],

  NETWORK_ERROR: ["h_auth_err_network_error", "No internet connection. Check your connection and try again."],
  SERVER_UNAVAILABLE: ["h_auth_err_server_unavailable", "The Elakay server cannot be reached right now. Please try again in a moment."],
  REQUEST_TIMEOUT: ["h_auth_err_request_timeout", "The server took too long to respond. Please try again."],
  TOO_MANY_REQUESTS: ["h_auth_err_too_many_requests", "Too many attempts. Please wait a moment and try again."],
  INTERNAL_SERVER_ERROR: ["h_auth_err_internal_server_error", "An unexpected error occurred. Please try again."],
};

// Codes other Elakay endpoints already send for the same situations.
const AUTH_ERROR_ALIASES = {
  DATABASE_UNAVAILABLE: "SERVER_UNAVAILABLE",
  INTERNAL_ERROR: "INTERNAL_SERVER_ERROR",
  RATE_LIMITED: "TOO_MANY_REQUESTS",
  TOKEN_NOT_FOUND: "INVALID_TOKEN",
};

export const AUTH_ERROR_CODES = Object.keys(AUTH_ERROR_TEXT);

export const resolveAuthErrorCode = (code) => {
  if (!code || typeof code !== "string") return null;
  const resolved = AUTH_ERROR_ALIASES[code] || code;
  return AUTH_ERROR_TEXT[resolved] ? resolved : null;
};

export const isKnownAuthErrorCode = (code) => Boolean(resolveAuthErrorCode(code));

// Text for `code` in the active language; `fallback` (e.g. the server's own
// message) only when the code is unknown to this build.
export const authErrorMessage = (code, fallback = "") => {
  const resolved = resolveAuthErrorCode(code);
  if (!resolved) return fallback || (code ? authErrorMessage("INTERNAL_SERVER_ERROR") : "");
  const [key, english] = AUTH_ERROR_TEXT[resolved];
  return elakayAutoText(key, english);
};

// What a page shows: the translated code when there is one, else the legacy
// string it already had.
export const displayAuthError = (errorCode, error) =>
  errorCode ? authErrorMessage(errorCode, typeof error === "string" ? error : "") : error;

const codeFromStatus = (status) => {
  if (status === 401) return "UNAUTHORIZED";
  if (status === 408 || status === 504) return "REQUEST_TIMEOUT";
  if (status === 429) return "TOO_MANY_REQUESTS";
  if (status === 502 || status === 503) return "SERVER_UNAVAILABLE";
  if (status >= 500) return "INTERNAL_SERVER_ERROR";
  return null;
};

// Debug trace for failed auth requests -- endpoint, status, code, server
// message and the resolved UI language. Never receives a request body, so
// passwords/tokens cannot end up here. Development builds only.
export const logAuthFailure = (endpoint, { status, code, message } = {}) => {
  if (process.env.NODE_ENV === "production" || process.env.NODE_ENV === "test") return;
  console.warn("[auth]", endpoint, { status, code, message, language: getElakayLanguageCode() });
};

// Error for a non-2xx auth response. Tolerates a non-JSON body (proxy error
// page) and always ends up with a code.
export const authResponseError = async (response, endpoint = "") => {
  let data = {};
  try {
    data = (await response.json()) || {};
  } catch {
    data = {};
  }
  const status = response.status;
  const code = resolveAuthErrorCode(data.code) || resolveAuthErrorCode(data.errorCode) || data.code || codeFromStatus(status) || null;
  const serverMessage = data.error || data.message || "";
  logAuthFailure(endpoint, { status, code, message: serverMessage });
  const message = isKnownAuthErrorCode(code) ? authErrorMessage(code, serverMessage) : (serverMessage || authErrorMessage("INTERNAL_SERVER_ERROR"));
  return Object.assign(new Error(message), {
    code,
    status,
    field: data.field,
    errorCode: data.errorCode,
    suspension: data.suspension,
    isAuthResponseError: true,
  });
};

// fetch() itself failed (no HTTP response at all) or was aborted by our timeout.
export const authNetworkErrorCode = (err) => {
  if (err?.name === "AbortError" || err?.name === "TimeoutError") return "REQUEST_TIMEOUT";
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "NETWORK_ERROR";
  return "SERVER_UNAVAILABLE";
};

// The `{ success:false, error, ... }` object every auth API helper returns on
// failure: `error` stays a (now translated) string for existing callers,
// `code` is what pages store to re-translate later.
export const authFailureResult = (err, endpoint = "") => {
  if (err?.isAuthResponseError) {
    return {
      success: false,
      error: err.message,
      code: err.code,
      status: err.status,
      field: err.field,
      errorCode: err.errorCode,
      suspension: err.suspension,
    };
  }
  const code = authNetworkErrorCode(err);
  logAuthFailure(endpoint, { status: 0, code, message: err?.message });
  return { success: false, error: authErrorMessage(code), code, status: 0 };
};

export const AUTH_REQUEST_TIMEOUT_MS = 30000;

// fetch() with a timeout, so a request the server never answers ends as
// REQUEST_TIMEOUT instead of a spinner forever.
export const fetchWithAuthTimeout = (url, options = {}, timeoutMs = AUTH_REQUEST_TIMEOUT_MS) => {
  if (typeof AbortController === "undefined") return fetch(url, options);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
};
